import { WeightedColorsArray } from 'Models';
import type { SelectionItem } from 'Models/facetRequest';
import type { PageSizeMode } from 'Models/pagination';
import type { RequestExtra } from 'Models/uiConfig/requestTypes';
import type { RuntimeValue } from 'Utils/typeGuards';

/**
 * A primitive/object value produced by the search server. Structurally identical
 * to `RuntimeValue` from `Utils/typeGuards`; kept as a separate alias to mark
 * "this value originated from the server protocol" at the call site.
 */
export type ServerValue = RuntimeValue;

interface ServerObject {
  [key: string]: ServerValue;
}

export interface SearchErrorInfo extends ServerObject {
  instance?: string;
  status?: number;
  timestamp?: string;
  userId?: string;
  sessionId?: string;
  isAnalyticsEnabled?: boolean;
}

export type RequestSelectionItem = SelectionItem;

export const SEARCH_ITEM_WIRE_FIELDS = {
  InternalCatalog: '_CatalogName',
  InternalDocId: '_docid',
  InternalScore: '_score',
  InternalRank: '_rank',
  InternalExplanation: '_explanation',
  Variants: '_variants',
  VariantOuterId: 'variant_id',
  VariantInnerId: 'id',
  Fitments: '_fitments',
  FitmentsTotal: '_fitments_total',
  FitmentOuterId: 'fitment_id',
  FitmentInnerId: 'id',
} as const;

export type SearchItemWireField = (typeof SEARCH_ITEM_WIRE_FIELDS)[keyof typeof SEARCH_ITEM_WIRE_FIELDS];

export interface SearchRequest {
  selection?: RequestSelectionItem[];
  query?: string;
  hiddenQuery?: string;
  filterQuery?: string;
  queryCorrectionLevel?: CorrectionLevelInput;
  pageNumber?: number;
  pageSize?: number;
  pageSizeMode?: PageSizeMode;
  mode?: string;
  sort?: string;
  catalog?: string;
  extra?: RequestExtra;
  variantsMode?: VariantsMode;
  referrer?: string;
  action?: string;
  defaultSearch?: string;
  explainScores?: boolean;
  maxCountByField?: Partial<Record<string, number>>;
  preferProductsOnly?: boolean;
}

export declare namespace ServerModel {
  /**
   * Server `Item` keys are case-insensitive before serialization, but JSON
   * object keys are case-sensitive once they reach the client.
   */
  interface GenericItem extends ServerObject {
    id?: string;
  }

  type NoneAction = { Type: 'None' };
  type RedirectAction = { Type: 'Redirect'; RedirectUrl: string };
  type ReplaceQueryAction = { Type: 'ReplaceQuery'; QueryReplacement: string };
  type AddSuggestionAction = { Type: 'AddSuggestion'; SuggestionText: string; SuggestionImage: string };
  type Action = NoneAction | RedirectAction | ReplaceQueryAction | AddSuggestionAction;

  interface AutocompletePopularSearchItem {
    Type: 'PopularSearch';
    Text: string;
    TextHighlight: string;
    SessionCount?: number;
  }

  interface AutocompleteFreetextItem {
    Type: 'Freetext';
    Text: string;
    TextHighlight: string;
    HitCount?: number;
  }

  interface AutocompleteProductItem extends SearchItem {
    Type: 'Product';
    SuggestionGroup: string;
    TotalHits: number;
    Redirect?: true;
  }

  interface AutocompleteCategoryItem extends SearchItem {
    Type: 'Category';
    Value: string;
    FacetValue: string;
    HitCount?: number | string;
    FieldName?: string;
    SuggestionGroup: string;
    ImageUrl?: string;
    Payload?: string;
    RedirectUrl?: string;
    Redirect?: true;
    ProductCategorySuggestion?: boolean;
  }

  type AutocompleteItem =
    | AutocompletePopularSearchItem
    | AutocompleteFreetextItem
    | AutocompleteProductItem
    | AutocompleteCategoryItem;

  type AutocompleteResponse = AutocompleteItem[];

  type FacetsResponse = Pick<SearchResponse, 'Facets'>;

  interface Facet {
    Value?: string;
    HitCount?: number;
    Term?: string;
    Selected?: boolean;
    Payload?: string;
    Url?: string;
    ImageUrl?: string;
    TreeLevel?: number;
  }

  type FacetCategory = {
    FieldName: string;
    DisplayName?: string;
    DisplayMode?: DisplayMode;
    AlwaysShowCount?: number;

    TotalHits?: number;
    TreeSeparator?: string | null;
    IsAffectedAutocomplete?: boolean;

    Ranges?: string[] | null;
    Values?: Facet[] | null;
    Selection?: Facet[] | null;
  };

  interface VerifyFitmentFacet {
    FieldName: FacetCategory['FieldName'];
    Selection: Pick<Facet, 'Value'>[] | null;
  }

  interface VerifyFitmentExtra {
    WheelTireVehicleInfo?: GenericItem;
  }

  interface VerifyFitmentCallbackResponse {
    Facets: VerifyFitmentFacet[];
    Items: VerifyFitmentProductData[];
    Extra?: GenericItem & VerifyFitmentExtra;
  }

  interface FitmentsResponse {
    Fitment: FitmentFieldValues;
    Fields: FitmentField[];
    Fitments: GenericItem[];
  }

  interface FitmentField {
    Name: string;
    Title: string;
  }

  interface Correction {
    State?: SearchStateValue;
    Original: string;
    Replace: string;
    Apply: boolean;
  }

  interface SearchResponseExtra {
    WheelTireVehicleInfo?: GenericItem;
    VehicleInfo?: GenericItem;
    AutoSyncVehicleId?: string;
    /** WheelWiz integration payload (lowercase per legacy wire). */
    vehicle?: GenericItem;
  }

  interface SearchResponse {
    Items: SearchItem[];
    Actions: Action[];
    Facets: FacetCategory[];
    Query: string;
    OriginalQuery: string;
    State: ResponseStateValue;
    TotalHits: number;
    Messages: string[];
    Corrections: Correction[];
    /**
     * Synthesized client-side by `httpClient.getErrorSearchResponse` when the
     * request never reaches a successful server response. The server itself
     * never emits this field — it lives in `ServerModel` only because the
     * client smuggles a `SearchResponse` shape through the same code path.
     * Underscore prefix mirrors other client-only `_*` keys on `SearchItem`.
     */
    _clientError?: SearchErrorInfo | null;
    Extra: GenericItem & SearchResponseExtra;
  }

  type SearchItem = GenericItem & {
    _CatalogName?: string;
    _docid?: number;
    _score?: number;
    _rank?: number;
    _explanation?: string;
    _variants?: GenericItem[];
    id?: string;
    fitsTheVehicle?: VerifyFitsValue;
    /**
     * Some client normalization paths expose variants without the server's
     * leading underscore. Raw search JSON uses `_variants`.
     */
    variants?: GenericItem[];
    variant_id?: string | number;
    _fitments?: GenericItem[];
    _fitments_total?: number;
    fitment_id?: string | number;
    _Color?: WeightedColorsArray;
    Styles?: string[];
  };
}

export enum VariantsMode {
  None = 'None',
  All = 'All',
  Preview = 'Preview',
  Filter = 'Filter',
  Strip = 'Strip',
}

export const CORRECTION_LEVEL_FLAGS = ['No', 'Spelling', 'Preemptive', 'Default'] as const;
export type CorrectionLevelFlag = (typeof CORRECTION_LEVEL_FLAGS)[number];
export type CorrectionLevel = CorrectionLevelFlag;
export type CorrectionLevelInput = CorrectionLevelFlag | readonly CorrectionLevelFlag[];

export const SEARCH_STATE_FLAGS = ['None', 'Normal', 'Corrected', 'Phonetic', 'Partial', 'Nothing'] as const;
export type SearchStateFlag = (typeof SEARCH_STATE_FLAGS)[number];
export type SearchStateValue = SearchStateFlag | `${SearchStateFlag}, ${string}`;

export const AUTOCOMPLETE_MODE_FLAGS = ['Freetext', 'Product', 'Category', 'PopularSearch'] as const;
export type AutocompleteModeFlag = (typeof AUTOCOMPLETE_MODE_FLAGS)[number];

export const SEARCH_RESPONSE_ERROR_STATES = [
  'timeout',
  'cancelled',
  'networkError',
  'badRequest',
  'unknown',
] as const;
type SearchResponseOtherStates = readonly ['', 'normal', 'corrected', 'partial', 'nothing'];
type SearchResponseStates = readonly [...SearchResponseOtherStates, ...typeof SEARCH_RESPONSE_ERROR_STATES];
type SearchResponseErrorStateValue = (typeof SEARCH_RESPONSE_ERROR_STATES)[number];
export type SearchResponseOtherStateValue = SearchResponseOtherStates[number];
export type SearchResponseStateValue = SearchResponseStates[number];
export type ResponseStateValue = SearchStateValue | SearchResponseErrorStateValue;

export type TrackEventTypeName =
  | 'None'
  | 'OpenPage'
  | 'ClosePage'
  | 'UserActive'
  | 'UserIdle'
  | 'ClickOnSearchResult'
  | 'ProductView'
  | 'AddToCart'
  | 'ConfirmOrder'
  | 'Search'
  | 'SuggestionSelection'
  | 'Reindex'
  | 'Autocomplete'
  | 'ClickOnRelatedProduct'
  | 'ExtractColor'
  | 'VerifyFitment'
  | 'GetProductFitments'
  | 'GetItem'
  | 'ThemeInfo'
  | 'ProductImpression';

export type VerifyFitsValue =
  | 'yes'
  | 'no'
  | 'universal'
  | 'nonVehicleProducts'
  | 'specify'
  | 'unknown'
  | 'aggressive';

export type VerifyFitmentProductData = Pick<ServerModel.SearchItem, 'id' | 'fitsTheVehicle'>;

export enum FitmentFieldValues {
  VehicleSpecific = 'Vehicle Specific',
  ManyFitments = 'Many Fitments',
  NoFitmentData = 'No Fitment Data',
  UniversalFit = 'Universal Fit',
  NonVehicleProducts = 'Non-Vehicle Products',
}

export enum DisplayMode {
  Default = 'Default',
  Checkbox = 'Checkbox',
  Radio = 'Radio',
  None = 'None',
  Ranges = 'Ranges',
  Slider = 'Slider',
  Select = 'Select',
  MultiSelect = 'MultiSelect',
  Size = 'Size',
  Toggle = 'Toggle',
  AdvancedColor = 'AdvancedColor',
  Switch = 'Switch',
  Tree = 'Tree',
}

export enum Message {
  UsageLimitExceeded = 'UsageLimitExceeded',
  DemoPlanActive = 'DemoPlanActive',
  TrialExpired = 'TrialExpired',
  NonVehicleResults = 'NonVehicleResults',
  NonVehicleSpecificResults = 'NonVehicleSpecificResults',
  UniversalFitResults = 'UniversalFitResults',
  IncompatibleParts = 'IncompatibleParts',
  AggressiveWheels = 'AggressiveWheels',
  VinVehicleNotFound = 'VinVehicleNotFound',
  VinVehicleNotFull = 'VinVehicleNotFull',
}
